import ReactEChartsCore from 'echarts-for-react/lib/core';
import * as echarts from 'echarts/core';
import { CallbackDataParams } from 'echarts/types/dist/shared';
import { useAppContext } from 'providers/AppProvider';
import { useContext } from 'react';

import dayjs from 'dayjs';
import { LineChart, PieChart } from 'echarts/charts';
import { TooltipComponent } from 'echarts/components';
import { color } from 'echarts';
import { useState } from 'react';
import { Modal, Button } from 'react-bootstrap';
echarts.use([TooltipComponent, PieChart, LineChart]);

export const tooltipFormatter = (
  params: CallbackDataParams[],
  dateFormatter = 'MMM DD',
  colorProperty: 'borderColor' | 'color' = 'borderColor'
) => {
  let tooltipItem = ``;
  params.forEach(el => {
    tooltipItem += `<div class='ms-1'>
        <h6 class="text-body-tertiary">
        <span class="d-inline-block rounded-circle me-2" style="height: 0.5rem; width: 0.5rem; background:${
          el[colorProperty]
        }"></span>
          ${el.seriesName} : ${el.value}${
            el.seriesName?.toLocaleLowerCase() === 'weight' ? 'Kg' : ''
          }
        </h6>
      </div>`;
  });
  return `<div>
            <p class='mb-2 text-body-tertiary'>
              ${
                dayjs(params[0].name).isValid()
                  ? dayjs(params[0].name).format('DD/MM/YYYY')
                  : params[0].name
              }
            </p>
            ${tooltipItem}
          </div>`;
};

const generateDateRange = (start: string, end: string) => {
  const startDate = dayjs(start);
  const endDate = dayjs(end);
  const dates: string[] = [];

  let current = startDate;
  while (current.isBefore(endDate) || current.isSame(endDate)) {
    dates.push(current.format('YYYY-MM-DD')); // store in same format as DB
    current = current.add(1, 'day');
  }

  return dates;
};

const getDefaultOptions = (
  getThemeColor: (name: string) => string,
  goals: any,
  dates: string[],
  yAxisData: number[],
  isInverse: boolean = false
) => ({
  color: [getThemeColor('success'), getThemeColor('info')],
  tooltip: {
    trigger: 'axis',
    backgroundColor: getThemeColor('body-bg'),
    bordercolor: getThemeColor('secondary-bg'),
    formatter: tooltipFormatter,
    axisPointer: {
      shadowStyle: {
        color: 'red'
      }
    }
  },
  legend: {
    bottom: '10',
    data: [
      {
        name: 'Weight',
        icon: 'roundRect'
      }
    ],
    itemWidth: 16,
    itemHeight: 8,
    itemGap: 10,
    inactiveColor: getThemeColor('quaternary-color'),
    inactiveBorderWidth: 0,
    textStyle: {
      color: getThemeColor('body-color'),
      fontWeight: 600,
      fontSize: 16,
      fontFamily: 'Nunito Sans'
    }
  },
  xAxis: {
    show: true,
    interval: 0,
    axisLine: {
      lineStyle: {
        type: 'solid',
        color: getThemeColor('tertiary-bg')
      }
    },
    axisLabel: {
      color: getThemeColor('body-color'),
      formatter: (data: string) => dayjs(data).format('DD-MM'),
      interval: 'auto',
      align: 'center',
      margin: 15,
      padding: [10, 0, 0, 0]
    },
    axisTick: {
      show: false,
      length: 15
    },
    splitLine: {
      show: true,
      lineStyle: {
        color: getThemeColor('tertiary-bg'),
        type: 'dashed'
      }
    },
    type: 'category',
    boundaryGap: true,
    data: dates,
    name: 'Date',
    nameLocation: 'middle',
    nameGap: 30,
    nameTextStyle: {
      color: getThemeColor('body-color'),
      fontWeight: 600,
      fontSize: 14,
      fontFamily: 'Nunito Sans',
      padding: [20, 0, 0, 0]
    }
  },
  yAxis: {
    type: 'value',
    show: true,
    min: yAxisData[0],
    max: yAxisData[yAxisData.length - 1],
    axisLine: {
      lineStyle: {
        type: 'solid',
        color: getThemeColor('tertiary-bg')
      }
    },
    axisLabel: {
      color: getThemeColor('body-color'),
      margin: 15,
      fontSize: 12.8,
      interval: 'auto',
      padding: [0, 10, 0, 0]
    },
    axisTick: {
      show: true,
      length: 1,
      alignWithLabel: true,
      lineStyle: {
        color: getThemeColor('tertiary-bg')
      }
    },
    splitLine: {
      show: true,
      lineStyle: {
        color: getThemeColor('tertiary-bg'),
        type: 'solid'
      }
    },
    inverse: false,
    name: 'Weight (kg)',
    nameLocation: 'middle',
    nameGap: 50,
    nameTextStyle: {
      color: getThemeColor('body-color'),
      fontWeight: 600,
      fontSize: 14,
      fontFamily: 'Nunito Sans'
    }
  },
  series: [
    {
      name: '',
      type: 'line',
      smooth: true,
      color: getThemeColor('primary'),
      data: goals.map((goal: any) => {
        return {
          value: goal.progress_value,
          name: dayjs(goal.date).format('YYYY-MM-DD')
        };
      })
    }
  ],
  grid: {
    right: 5,
    left: 40,
    bottom: '15%',
    top: 20,
    containLabel: true
  }
});

const WeightChart = (props: any) => {
  const { getThemeColor } = useAppContext();
  const selectedGoal = props.selectedGoal;

  const [showEditModal, setShowEditModal] = useState(false);
  const [editValue, setEditValue] = useState('');
  const [editId, setEditId] = useState<number | null>(null);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);

  const generateDateRange = (startDate: string, endDate: string) => {
    const dates = [];
    let currentDate = dayjs(startDate);
    const end = dayjs(endDate);

    while (currentDate.isBefore(end) || currentDate.isSame(end)) {
      dates.push(currentDate.format('YYYY-MM-DD'));
      currentDate = currentDate.add(1, 'day');
    }
    return dates;
  };

  if (!selectedGoal) {
    return <div>Loading...</div>;
  }

  console.log(props.goalsData, 'goals data in weight chart');

  // Get recorded dates from goal progress data
  const recordedDates = props.goalsData.map((goal: any) =>
    dayjs(goal.date).format('YYYY-MM-DD')
  );

  // Get the last recorded date and add 3 more dates
  const lastRecordedDate = recordedDates[recordedDates.length - 1];
  const endDate = dayjs(lastRecordedDate).add(3, 'day').format('YYYY-MM-DD');

  // Generate dates from first recorded to last recorded + 3 days
  const firstRecordedDate = recordedDates[0];
  const dates = generateDateRange(firstRecordedDate, endDate);

  // Filter to show only recorded dates
  const displayDates = dates.filter(date => recordedDates.includes(date));

  // Calculate width based on record count
  const recordCount = props.goalsData.length;
  let chartWidth = '100%';

  if (recordCount <= 6) {
    chartWidth = '50%';
  } else if (recordCount <= 15) {
    chartWidth = '75%';
  } else {
    chartWidth = '100%';
  }

  const currentWeight =
    props.goalsData.length > 0 ? props.goalsData[0].progress_value : null;
  let targetWeight = selectedGoal.target_value;

  const currentWeightNum = parseFloat(currentWeight);
  const targetWeightNum = parseFloat(targetWeight);
  const isInverse = currentWeightNum > targetWeightNum;

  const yAxisData: number[] = [];

  // set max value of weight from goals data
  const maxWeight = Math.max(
    ...props.goalsData.map((goal: any) => goal.progress_value)
  );
  const minWeight = Math.min(
    ...props.goalsData.map((goal: any) => goal.progress_value)
  );

  targetWeight = targetWeightNum > maxWeight ? targetWeightNum : maxWeight;

  const lowerBound = Math.max(20, Math.min(minWeight, targetWeightNum) - 10); // Min 20kg, with 20kg buffer
  const upperBound = Math.min(400, Math.max(maxWeight, targetWeightNum) + 10); // Max 400kg, with 20kg buffer

  if (!isNaN(lowerBound) && !isNaN(upperBound)) {
    for (let j = lowerBound; j <= upperBound; j++) {
      yAxisData.push(Number(j));
    }
  }

  const options = getDefaultOptions(
    getThemeColor,
    props.goalsData,
    displayDates,
    yAxisData
  );

  const handleEdit = (item: any) => {
    setEditId(item.id);
    setEditValue(item.progress_value);
    setShowEditModal(true);
  };

  const handleUpdate = async () => {
    if (!editId) return;

    await props.onUpdate(editId, Number(editValue));

    setShowEditModal(false);
    setEditId(null);
  };

  const handleDeleteClick = (item: any) => {
    setDeleteId(item.id);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteId) return;

    await props.onDelete(deleteId);

    setShowDeleteModal(false);
    setDeleteId(null);
  };

  return (
    <>
      <ReactEChartsCore
        echarts={echarts}
        option={options}
        style={{ minHeight: '320px', width: chartWidth }}
      />

      <table className="table table-bordered mt-4">
        <thead>
          <tr>
            <th>Weight (kg) </th>
            <th>Date</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          {props.goalsData.map((item: any, index: number) => (
            <tr key={index}>
              <td>{item.progress_value}</td>
              <td>{dayjs(item.date).format('DD-MM-YYYY')}</td>
              <td>
                <button
                  className="btn btn-sm btn-primary"
                  onClick={() => handleEdit(item)}
                >
                  Edit
                </button>
                <button
                  className="btn btn-sm btn-danger ms-4"
                  onClick={() => handleDeleteClick(item)}
                >
                  DELETE
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <Modal
        show={showEditModal}
        onHide={() => setShowEditModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Update Weight</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <div className="mb-3">
            <label className="form-label">Weight (kg)</label>
            <input
              type="number"
              className="form-control"
              value={editValue}
              onChange={e => setEditValue(e.target.value)}
            />
          </div>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleUpdate}>
            Save
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Delete Weight</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <p>Are you sure you want to delete this record?</p>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDeleteConfirm}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default WeightChart;
